// api/events.js - Event Management API

import { apiClient } from './client';

class EventAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getCampaignEvents(campaignId) {
    return this.client.request(`/api/events?campaign_id=${campaignId}`);
  }

  async getEvent(eventId) {
    return this.client.request(`/api/events/${eventId}`);
  }

  async createEvent(campaignId, eventData) {
    return this.client.request('/api/events', {
      method: 'POST',
      body: JSON.stringify({
        campaign_id: campaignId,
        event_type: eventData.type,
        event_title: eventData.title,
        event_description: eventData.description,
        occurred_on_day: eventData.day,
        ...eventData,
      }),
    });
  }

  async updateEvent(eventId, updates) {
    return this.client.request(`/api/events/${eventId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteEvent(eventId) {
    return this.client.request(`/api/events/${eventId}`, {
      method: 'DELETE',
    });
  }

  // ========== EVENT CREATION HELPERS ==========

  async logStoryEvent(campaignId, day, title, description) {
    return this.createEvent(campaignId, {
      type: 'story',
      title,
      description,
      day,
    });
  }

  async logCombatEvent(campaignId, day, title, description, combatData = {}) {
    return this.createEvent(campaignId, {
      type: 'combat',
      title,
      description,
      day,
      ...combatData,
    });
  }

  async logResourceEvent(campaignId, day, title, description, resourceChanges = {}) {
    return this.createEvent(campaignId, {
      type: 'resource',
      title,
      description,
      day,
      resource_changes: JSON.stringify(resourceChanges),
    });
  }

  async logSquadEvent(campaignId, day, title, description, squadData = {}) {
    return this.createEvent(campaignId, {
      type: 'squad',
      title,
      description,
      day,
      ...squadData,
    });
  }

  // ========== QUERIES ==========

  async getEventsByDay(campaignId, day) {
    const events = await this.getCampaignEvents(campaignId);
    return events.filter(e => e.occurred_on_day === day);
  }

  async getEventsByType(campaignId, eventType) {
    const events = await this.getCampaignEvents(campaignId);
    return events.filter(e => e.event_type === eventType);
  }

  async getRecentEvents(campaignId, limit = 10) {
    const events = await this.getCampaignEvents(campaignId);
    return events
      .sort((a, b) => b.occurred_on_day - a.occurred_on_day)
      .slice(0, limit);
  }

  async getEventsBetweenDays(campaignId, startDay, endDay) {
    const events = await this.getCampaignEvents(campaignId);
    return events.filter(e => 
      e.occurred_on_day >= startDay && e.occurred_on_day <= endDay
    );
  }

  // ========== EVENT TIMELINE ==========

  async getEventTimeline(campaignId) {
    const events = await this.getCampaignEvents(campaignId);
    
    // Group events by day
    const timeline = {};
    events.forEach(event => {
      const day = event.occurred_on_day;
      if (!timeline[day]) {
        timeline[day] = [];
      }
      timeline[day].push(event);
    });
    
    return timeline;
  }

  async getEventSummary(campaignId) {
    const events = await this.getCampaignEvents(campaignId);
    
    return {
      total: events.length,
      byType: {
        story: events.filter(e => e.event_type === 'story').length,
        combat: events.filter(e => e.event_type === 'combat').length,
        resource: events.filter(e => e.event_type === 'resource').length,
        squad: events.filter(e => e.event_type === 'squad').length,
      },
      firstDay: Math.min(...events.map(e => e.occurred_on_day)),
      lastDay: Math.max(...events.map(e => e.occurred_on_day)),
    };
  }
}

// Export singleton instance
export const eventAPI = new EventAPI(apiClient);

// Also export class
export default EventAPI;